/**
 * Reddit's ad pixel, which tells Reddit Ads which visits and sign-ups came from our ads (REDDIT_PIXEL_ID on the server).
 * It runs on the public pages and the sign-up page, for browsers that weren't signed in here, and never inside the app:
 * if it was loaded when someone signs in, AppShell reloads the page without it. Where the law wants consent first
 * (`askConsent` from /api/site, by country) the visitor is asked; everyone else can turn it off under "Your privacy
 * choices" in the footer, and a browser that sends Global Privacy Control never runs it. The privacy policy describes
 * it (Legal.tsx, "Cookies and browser storage").
 */
import { useEffect, useSyncExternalStore } from 'react';
import { Link, useLocation } from 'react-router';
import { cx } from '../components/ui';
import { sessionHint, useHydrated, useSite, type Site } from './hooks';

type Rdt = ((...args: unknown[]) => void) & { callQueue: unknown[][]; sendEvent?: (...args: unknown[]) => void };
declare global {
  interface Window {
    rdt?: Rdt;
  }
  interface Navigator {
    globalPrivacyControl?: boolean;
  }
}

const SCRIPT = 'https://www.redditstatic.com/ads/pixel.js';
const CHOICE = 'raremargin:ad-pixel';
type Choice = 'on' | 'off' | null;

function storedChoice(): Choice {
  if (typeof window === 'undefined') return null;
  try {
    const v = localStorage.getItem(CHOICE);
    return v === 'on' || v === 'off' ? v : null;
  } catch {
    return null;
  }
}

// The visitor's choice and whether the choices panel is open, shared by the pixel, the banner and the footer link.
let state = { choice: storedChoice(), open: false };
const listeners = new Set<() => void>();
const update = (next: Partial<typeof state>) => {
  state = { ...state, ...next };
  for (const l of listeners) l();
};
const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => void listeners.delete(l);
};
// The server renders the public pages (ssr.tsx) without knowing this browser's choice.
const unasked: typeof state = { choice: null, open: false };
const useChoices = () => useSyncExternalStore(subscribe, () => state, () => unasked);

const gpc = () => navigator.globalPrivacyControl === true;
let failed = false;

function allowed(site: Site, choice: Choice) {
  if (!site.redditPixelId || gpc() || sessionHint.get()) return false;
  return choice === 'on' || (choice === null && !site.askConsent);
}

/** Reddit's loader, from our bundle rather than an inline script (the CSP allows no inline scripts). */
function load(id: string) {
  if (window.rdt) return;
  const rdt: Rdt = Object.assign((...args: unknown[]) => (rdt.sendEvent ? rdt.sendEvent(...args) : void rdt.callQueue.push(args)), { callQueue: [] as unknown[][] });
  window.rdt = rdt;
  const script = document.createElement('script');
  script.src = SCRIPT;
  script.async = true;
  // An ad blocker stops it, so a sign-up doesn't wait for it.
  script.onerror = () => void (failed = true);
  document.head.append(script);
  rdt('init', id);
}

/** Whether the pixel is in this page. */
export const pixelLoaded = () => !!window.rdt;

/** On a public page: loads the pixel when it may run, and counts a visit for each page. */
export function usePixelPageVisit() {
  const site = useSite().data;
  const { choice } = useChoices();
  const { pathname } = useLocation();
  useEffect(() => {
    // The stored choice itself, not the rendered one: while the browser takes over the server's HTML, the render has
    // none yet, and someone who said no must not be counted in that moment.
    if (!site || !allowed(site, state.choice)) return;
    load(site.redditPixelId);
    window.rdt!('track', 'PageVisit');
  }, [site, choice, pathname]);
}

/**
 * Counts a sign-up. Resolves once Reddit's script has the event, at most about 2.5 s later, so the app can open (and
 * reload without the pixel) after it: the script sends with `fetch(…, { keepalive })`, which outlives the page.
 */
export async function trackSignUp() {
  const rdt = window.rdt;
  if (!rdt) return;
  rdt('track', 'SignUp');
  for (const until = Date.now() + 2000; !rdt.sendEvent && !failed && Date.now() < until; ) await new Promise((r) => setTimeout(r, 100));
  if (rdt.sendEvent) await new Promise((r) => setTimeout(r, 500));
}

/** Opens the choices panel (the footer's "Your privacy choices"). */
export const openPixelChoices = () => update({ open: true });

function choose(choice: 'on' | 'off') {
  try {
    localStorage.setItem(CHOICE, choice);
  } catch {
    // Storage off (private mode): the choice lasts for this page.
  }
  if (choice === 'off') {
    // Reddit's cookies (set on the whole domain for 90 days) and anything it stored.
    const domain = location.hostname.split('.').slice(-2).join('.');
    for (const name of document.cookie.split(';').map((c) => c.split('=')[0]!.trim()))
      if (name.startsWith('_rdt_')) for (const d of ['', `;domain=.${domain}`]) document.cookie = `${name}=;max-age=0;path=/${d}`;
    try {
      for (const k of Object.keys(localStorage)) if (k.startsWith('_rdt')) localStorage.removeItem(k);
    } catch {
      // As above.
    }
    // A script can't be unloaded: a fresh page is one without it.
    if (pixelLoaded()) return location.reload();
  }
  update({ choice, open: false });
}

/** The question, for visitors who must be asked first, and the panel "Your privacy choices" opens. */
export function PixelChoices() {
  const site = useSite().data;
  const { choice, open } = useChoices();
  // Only once in the browser: the banner depends on this browser's choice, its privacy signal and the visitor's country.
  const hydrated = useHydrated();
  if (!hydrated || !site?.redditPixelId || sessionHint.get()) return null;
  const asking = choice === null && site.askConsent && !gpc();
  if (!asking && !open) return null;
  const button = 'h-9 rounded-[10px] border px-4 text-[14px] font-[540] transition-colors';
  return (
    <section
      aria-labelledby="ad-pixel-title"
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-[540px] rounded-[16px] border border-site-rule bg-site-white p-5 font-display text-site-ink shadow-[0_18px_40px_-16px_rgb(27_26_24/0.4)] sm:bottom-5"
    >
      <h2 id="ad-pixel-title" className="text-[16px] font-[600] tracking-[-0.01em]">
        {asking ? 'Allow Reddit’s ad pixel?' : 'Reddit’s ad pixel'}
      </h2>
      <p className="mt-1.5 text-[14px] leading-relaxed text-site-ink-2">
        It tells us which visits and sign-ups come from our ads on Reddit. It sets cookies and sends Reddit the pages you view on this website, never anything from inside the app.{' '}
        <Link to="/privacy#cookies" className="underline underline-offset-2 hover:text-site-ink">
          Privacy policy
        </Link>
      </p>
      {gpc() ? (
        <p className="mt-2 text-[14px] text-site-ink-2">Your browser sends Global Privacy Control, so the pixel stays off.</p>
      ) : (
        !asking && <p className="mt-2 text-[14px] text-site-ink-2">It is {allowed(site, choice) ? 'on' : 'off'} in this browser.</p>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        {!gpc() && (
          <>
            <button type="button" onClick={() => choose('on')} className={cx(button, 'border-site-ink bg-site-ink text-site-white hover:bg-site-ink/85')}>
              Allow
            </button>
            <button type="button" onClick={() => choose('off')} className={cx(button, 'border-site-ink bg-site-ink text-site-white hover:bg-site-ink/85')}>
              Don’t allow
            </button>
          </>
        )}
        {!asking && (
          <button type="button" onClick={() => update({ open: false })} className={cx(button, 'border-site-rule-strong text-site-ink-2 hover:bg-site-oat')}>
            Close
          </button>
        )}
      </div>
    </section>
  );
}
